import { useEffect, useRef, useState } from "react";
import { styles } from "../../styles";
import { ErrorNote } from "../common";
import { useShop } from "../../context/ShopContext";
import { useToast } from "../../context/ToastContext";
import { saveShopConfig } from "../../data/config";
import { uploadLogo, removeLogo, MAX_LOGO_BYTES, ALLOWED_LOGO_TYPES } from "../../data/storage";
import { logAudit } from "../../data/audit";
import {
  palettePresets, sagePalette,
} from "../../lib/constants";
import {
  applyPalette, PALETTE_TOKENS, paletteContrastChecks, isValidHex,
} from "../../lib/palette";
import type { Palette } from "../../lib/types";

export function BrandingCard() {
  const { config } = useShop();
  const notify = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [shopName, setShopName] = useState(config.shopName);
  const [palette, setPalette] = useState<Palette>(config.palette);
  const [preset, setPreset] = useState(config.palettePreset);
  const [customize, setCustomize] = useState(config.palettePreset === "custom");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => setShopName(config.shopName), [config.shopName]);
  useEffect(() => { setPalette(config.palette); setPreset(config.palettePreset); }, [config.palette, config.palettePreset]);

  // Live preview while editing. On unmount, restore the saved palette so an
  // abandoned edit does not leave the page mis-themed.
  useEffect(() => {
    applyPalette(palette);
    return () => applyPalette(config.palette);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [palette]);

  const checks = paletteContrastChecks(palette);
  const failures = checks.filter((c) => !c.passes);
  const dirty =
    shopName !== config.shopName ||
    preset !== config.palettePreset ||
    JSON.stringify(palette) !== JSON.stringify(config.palette);

  const choosePreset = (name: string) => {
    setPreset(name);
    setCustomize(false);
    setPalette(palettePresets[name] ?? sagePalette);
  };

  const editToken = (key: keyof Palette, value: string) => {
    setPalette((p) => ({ ...p, [key]: value }));
    setPreset("custom");
  };

  const save = async () => {
    setError(null);
    for (const t of PALETTE_TOKENS) {
      if (!isValidHex(palette[t.key])) {
        setError(`The ${t.label.toLowerCase()} color is not a valid hex value.`);
        return;
      }
    }
    setBusy(true);
    try {
      await saveShopConfig({ shopName: shopName.trim() || "Radiance", palette, palettePreset: preset });
      void logAudit("Updated branding and theme");
      notify("Branding saved.");
    } catch {
      setError("We could not save your branding. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (file: File | undefined) => {
    setError(null);
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setError("Use a PNG, JPG, SVG, or WebP image for the logo.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError("That image is over 2 MB. Please choose a smaller file.");
      return;
    }
    setBusy(true);
    try {
      const url = await uploadLogo(file);
      await saveShopConfig({ logoUrl: url });
      void logAudit("Updated the logo");
      notify("Logo updated.");
    } catch {
      setError("We could not upload that logo. Please try again.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearLogo = async () => {
    setError(null);
    setBusy(true);
    try {
      await removeLogo();
      await saveShopConfig({ logoUrl: "" });
      void logAudit("Removed the logo");
      notify("Logo removed.");
    } catch {
      setError("We could not remove the logo. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.card}>
      <h2 style={styles.cardTitle}>Branding &amp; theme</h2>
      <p style={styles.sub}>Set your shop name, logo, and colors. Customers see these live.</p>
      {error && <ErrorNote>{error}</ErrorNote>}

      {/* Shop name */}
      <div style={styles.field}>
        <label htmlFor="brand-name" style={styles.label}>Shop name</label>
        <input
          id="brand-name" value={shopName} onChange={(e) => setShopName(e.target.value)}
          className="input" style={styles.input} placeholder="Radiance"
        />
      </div>

      {/* Logo */}
      <span style={styles.label}>Logo</span>
      <div style={styles.logoPreviewWrap}>
        {config.logoUrl ? (
          <img src={config.logoUrl} alt="Current shop logo" style={styles.logoPreview} />
        ) : (
          <div style={{ ...styles.logoPreview, display: "grid", placeItems: "center", color: "var(--accent)" }} aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <circle cx="7" cy="8" r="2.1" fill="currentColor" /><circle cx="12" cy="6.4" r="2.1" fill="currentColor" />
              <circle cx="17" cy="8" r="2.1" fill="currentColor" />
              <path d="M12 11c-3 0-5 2.2-5 4.6 0 1.7 1.4 2.6 3 2.2 1.3-.3 2.7-.3 4 0 1.6.4 3-.5 3-2.2C17 13.2 15 11 12 11z" fill="currentColor" />
            </svg>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="ghost" style={styles.ghostSm} onClick={() => fileRef.current?.click()} disabled={busy}>
            {config.logoUrl ? "Replace logo" : "Upload logo"}
          </button>
          {config.logoUrl && (
            <button className="ghost" style={styles.ghostSm} onClick={clearLogo} disabled={busy}>Remove</button>
          )}
        </div>
        <input
          ref={fileRef} type="file" accept={ALLOWED_LOGO_TYPES.join(",")}
          onChange={(e) => onFile(e.target.files?.[0])} style={{ display: "none" }} aria-label="Upload logo file"
        />
      </div>
      <p style={styles.hint}>PNG, JPG, SVG, or WebP, up to 2 MB.</p>

      {/* Palette presets */}
      <div style={{ marginTop: 18 }}>
        <span style={styles.label}>Color theme</span>
        <div style={styles.presetRow} role="group" aria-label="Color presets">
          {Object.entries(palettePresets).map(([name, p]) => (
            <button
              key={name} className="preset" aria-pressed={preset === name && !customize}
              style={{ ...styles.preset, ...(preset === name && !customize ? styles.presetOn : {}) }}
              onClick={() => choosePreset(name)}
            >
              <span style={styles.presetDots}>
                <span style={{ ...styles.presetDot, background: p.accent }} />
                <span style={{ ...styles.presetDot, background: p.accentSoft }} />
                <span style={{ ...styles.presetDot, background: p.bg, border: "1px solid var(--line)" }} />
              </span>
              {name}
            </button>
          ))}
          <button
            className="preset" aria-pressed={customize}
            style={{ ...styles.preset, ...(customize ? styles.presetOn : {}) }}
            onClick={() => setCustomize((v) => !v)}
          >
            Customize
          </button>
        </div>

        {customize && (
          <div style={styles.tokenGrid}>
            {PALETTE_TOKENS.map((t) => (
              <div key={t.key} style={styles.tokenItem}>
                <input
                  type="color" value={isValidHex(palette[t.key]) ? palette[t.key] : "#000000"}
                  onChange={(e) => editToken(t.key, e.target.value)}
                  style={styles.tokenColor} aria-label={`${t.label} color picker`}
                  title={t.label}
                />
                <div>
                  <span style={styles.tokenLabel}>{t.label}</span>
                  <input
                    value={palette[t.key]} onChange={(e) => editToken(t.key, e.target.value)}
                    style={styles.tokenHex} aria-label={`${t.label} hex value`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Readability guardrail */}
        {failures.length > 0 ? (
          <div style={styles.warnBox} role="alert">
            <span style={styles.warnTitle}>Low contrast may be hard to read (below WCAG AA):</span>
            {failures.map((c) => (
              <span key={c.label} style={styles.warnItem}>
                {c.label}: {c.ratio ? c.ratio.toFixed(2) : "?"}:1 (needs {c.threshold}:1)
              </span>
            ))}
          </div>
        ) : (
          <div style={styles.okBox}>
            <span aria-hidden="true">✓</span> Colors pass WCAG AA contrast.
          </div>
        )}
      </div>

      <div style={styles.msgActions}>
        <button className="primary" style={styles.primarySm} onClick={save} disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save branding"}
        </button>
      </div>
    </div>
  );
}
